import { PaymentMethod } from 'types';

/**
 * Chaves de cartão usadas no pagamento combinado. O checkout guarda o método
 * selecionado como `CombinedPaymentMethodKey`, e nesse modo o cartão não vale
 * `PaymentMethod.CREDIT_CARD` e sim `credit_card_1` / `credit_card_2`
 * (`Payment.tsx` faz essa normalização em vários pontos).
 */
export const COMBINED_CREDIT_CARD_KEYS = [
  'credit_card_1',
  'credit_card_2',
] as const;

/**
 * FIN-7470: qualquer regra nova de 3DS vale **só para cartão** (AC P1-4.2 — pix,
 * boleto, NuPay e Apple Pay não mudam, com ou sem 3DS na oferta). Comparar
 * apenas contra `PaymentMethod.CREDIT_CARD` deixaria o modo combinado / dois
 * cartões de fora, porque ali o valor em runtime é `credit_card_1`.
 *
 * `DEFAULT_CREDIT_CARD` (cartão salvo) fica de fora de propósito: recompra com
 * cartão salvo está fora do escopo da feature e continua exatamente como hoje.
 */
export const isCreditCardPaymentMethod = (
  method?: PaymentMethod | string | null
): boolean =>
  method === PaymentMethod.CREDIT_CARD ||
  (COMBINED_CREDIT_CARD_KEYS as readonly string[]).includes(
    method as string
  );

/**
 * FIN-7470 (AC P1-4.3 / Out of Scope): o 3DS desta feature é de **cartão
 * único**. Pagamento combinado (mais de uma perna) está declarado fora do
 * escopo e precisa continuar exatamente como hoje.
 *
 * A regra olha a seleção INTEIRA, e não o primeiro método do array: a ordem
 * ali é a ordem de clique do comprador (`Payment.tsx`), então decidir pela
 * posição fazia duas compras idênticas autenticarem ou não conforme o botão
 * que ele apertou primeiro. Aqui o resultado é o mesmo nos dois ordenamentos.
 */
export const hasSingleCreditCardSelection = (
  methods?: readonly (PaymentMethod | string | null | undefined)[] | null
): boolean =>
  (methods?.length ?? 0) === 1 && isCreditCardPaymentMethod(methods?.[0]);
